import React, { useEffect } from 'react';
import { X } from 'lucide-react';

const overlay = { background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(10px)' };
const panel = { background: '#141414', border: '1px solid rgba(201,168,76,0.18)' };
const h2 = { fontSize: '20px', fontWeight: 700, color: '#F5F5F0', marginBottom: '10px' };
const p = { fontSize: '14px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.8, marginBottom: '10px' };
const li = { fontSize: '14px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.7 };
const eyebrow = { fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', color: '#C9A84C', fontWeight: 600 };
const strong = { color: '#F5F5F0' };

const Section = ({ n, title, children }) => (
  <section className="mb-8">
    <h2 className="font-display" style={h2}>{n}. {title}</h2>
    <div className="font-body">{children}</div>
  </section>
);

const TermsOfService = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" style={overlay} data-testid="terms-modal">
      <div className="min-h-screen py-10 px-4">
        <div className="mx-auto max-w-3xl">
          <div className="flex justify-between items-start mb-8">
            <div>
              <span className="font-body" style={eyebrow}>Legal</span>
              <h1 className="font-display mt-2" style={{ fontSize: 'clamp(28px,4vw,40px)', fontWeight: 700, color: '#F5F5F0' }}>Términos de Servicio</h1>
              <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>Yamauchi Ramen · Última actualización: 8 de agosto de 2026</p>
            </div>
            <button onClick={onClose} className="text-[#C2C1B6] hover:text-[#C9A84C] transition-colors p-2" data-testid="terms-close" aria-label="Cerrar">
              <X className="w-7 h-7" />
            </button>
          </div>

          <div className="p-6 sm:p-8" style={panel}>
            <Section n="1" title="Aceptación de los términos">
              <p style={p}>
                Al usar el sitio web de Yamauchi Ramen, crear una cuenta, ordenar como invitado o realizar un pedido, aceptas estos Términos de Servicio y nuestra Política de Privacidad. Si no estás de acuerdo, por favor no utilices nuestros servicios.
              </p>
            </Section>

            <Section n="2" title="Cuentas de usuario">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Puedes crear una cuenta con email y contraseña, o iniciar sesión con Google. También puedes ordenar como invitado proporcionando nombre, teléfono y email.</li>
                <li style={li}>Te comprometes a proporcionar información veraz y actualizada.</li>
                <li style={li}>Eres responsable de mantener la confidencialidad de tus credenciales y de la actividad en tu cuenta.</li>
                <li style={li}>Debes ser mayor de 18 años para crear una cuenta y realizar pedidos.</li>
                <li style={li}>Al registrarte puedes optar por recibir <strong style={strong}>ofertas y novedades por email</strong>; es opcional y puedes darte de baja en cualquier momento desde tu perfil o desde el propio correo.</li>
              </ul>
            </Section>

            <Section n="3" title="Pedidos y ventana de tiempo">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Los pedidos de entrega se pueden realizar desde el <strong style={strong}>jueves a las 8:00 AM hasta el viernes a las 10:45 AM</strong> (hora de Puerto Rico).</li>
                <li style={li}>La entrega se realiza los <strong style={strong}>viernes de 11:00 AM a 12:00 PM</strong>.</li>
                <li style={li}>La entrega está disponible únicamente a los <strong style={strong}>lugares de nuestra lista de puntos autorizados</strong>, que seleccionas al ordenar.</li>
                <li style={li}>Los artículos están sujetos a disponibilidad. El menú de entrega es limitado y puede diferir del menú del local.</li>
              </ul>
            </Section>

            <Section n="4" title="Precios y pagos">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Todos los precios están en dólares estadounidenses (USD) e incluyen los impuestos aplicables según la ley.</li>
                <li style={li}>El pago se realiza por adelantado con tarjeta a través de Stripe. El pedido se confirma solo tras el pago exitoso.</li>
                <li style={li}>Tras el pago recibirás por email un <strong style={strong}>recibo/confirmación</strong> de tu pedido y avisos de estado (por ejemplo, "en camino").</li>
                <li style={li}>Nos reservamos el derecho de corregir errores de precio evidentes y de cancelar pedidos afectados con reembolso.</li>
              </ul>
            </Section>

            <Section n="5" title="Cancelaciones y reembolsos">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Puedes solicitar la cancelación con reembolso completo antes de que el pedido entre en preparación (antes del cierre de la ventana de pedidos).</li>
                <li style={li}>Una vez que el pedido está en preparación o en ruta, no es posible cancelarlo.</li>
                <li style={li}>Si no podemos completar la entrega por causas atribuibles a nosotros, recibirás un reembolso.</li>
                <li style={li}>Si la entrega falla porque no estás disponible o proporcionaste datos incorrectos, es posible que no aplique reembolso.</li>
              </ul>
            </Section>

            <Section n="6" title="Entrega">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Debes estar disponible en el lugar de entrega seleccionado durante la ventana de entrega.</li>
                <li style={li}>El aviso de "llega en ~20 minutos" es una estimación y puede variar por tráfico, clima u orden de la ruta.</li>
                <li style={li}>Es tu responsabilidad seleccionar el lugar de entrega correcto y estar disponible para recibir el pedido.</li>
              </ul>
            </Section>

            <Section n="7" title="Bebidas alcohólicas">
              <p style={p}>
                No se venden ni entregan bebidas alcohólicas a través del servicio de entrega. La venta de alcohol se realiza únicamente en el local, exclusivamente a mayores de 18 años con identificación válida, conforme a la ley de Puerto Rico.
              </p>
            </Section>

            <Section n="8" title="Uso aceptable">
              <p style={p}>Al usar el sitio te comprometes a no:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Proporcionar información falsa o suplantar a otra persona.</li>
                <li style={li}>Intentar vulnerar la seguridad del sitio o realizar fraude de pagos.</li>
                <li style={li}>Usar nuestro contenido con fines comerciales sin autorización.</li>
              </ul>
            </Section>

            <Section n="9" title="Servicios de terceros y comunicaciones">
              <p style={p}>
                El procesamiento de pagos (Stripe), el inicio de sesión con Google y el envío de correos (Resend) se rigen también por los términos y políticas de dichas empresas; no somos responsables por sus servicios. Los correos transaccionales (recibos y avisos de pedido) forman parte del servicio; los correos de marketing solo se envían con tu consentimiento y puedes cancelarlos cuando quieras.
              </p>
            </Section>

            <Section n="10" title="Alergias y restricciones dietéticas">
              <p style={p}>
                Es responsabilidad del cliente informar sobre alergias. Aunque hacemos nuestro mejor esfuerzo, no podemos garantizar que nuestros productos estén completamente libres de alérgenos (p. ej., gluten, soya, huevo, mariscos, sésamo).
              </p>
            </Section>

            <Section n="11" title="Limitación de responsabilidad">
              <p style={p}>
                En la máxima medida permitida por la ley, Yamauchi Ramen no será responsable por daños indirectos o consecuenciales. Nuestra responsabilidad total no excederá el monto pagado por el pedido correspondiente.
              </p>
            </Section>

            <Section n="12" title="Propiedad intelectual">
              <p style={p}>
                El logotipo, la marca "Yamauchi", fotografías, videos, textos y el diseño del sitio están protegidos por derechos de autor y son propiedad de Yamauchi Ramen.
              </p>
            </Section>

            <Section n="13" title="Ley aplicable">
              <p style={p}>
                Estos términos se rigen por las leyes del Estado Libre Asociado de Puerto Rico y las leyes federales aplicables de los Estados Unidos. Cualquier disputa se resolverá en los tribunales competentes de Puerto Rico.
              </p>
            </Section>

            <Section n="14" title="Cambios y contacto">
              <p style={p}>
                Podemos modificar estos términos en cualquier momento; el uso continuado implica su aceptación. Para consultas:
              </p>
              <div className="p-4" style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.14)' }}>
                <p style={{ ...p, marginBottom: '4px', color: '#F5F5F0', fontWeight: 500 }}>Yamauchi Ramen</p>
                <p style={{ ...p, marginBottom: '2px' }}>Urb. C1 Cll 14, Cayey, PR 00633</p>
                <p style={{ ...p, marginBottom: '2px' }}>Tel: +1 (939) 593-3353</p>
                <p style={{ ...p, marginBottom: '2px' }}>Email: yamauchi.cayey@gmail.com</p>
                <p style={{ ...p, marginBottom: '0' }}>Instagram: @yamauchiramenpr</p>
              </div>
            </Section>
          </div>

          <div className="text-center mt-8">
            <button onClick={onClose} className="font-body uppercase" style={{ border: '1px solid #C9A84C', color: '#C9A84C', padding: '13px 30px', fontSize: '12px', letterSpacing: '2px', fontWeight: 600, background: 'transparent', cursor: 'pointer' }} data-testid="terms-close-bottom">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
